import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import apiService from "../services/api";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://printformee.com/api";

const resolveImageUrl = (path) => {
  if (!path) return null;
  if (typeof path !== "string") return null;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  if (path.startsWith("/")) {
    return `${API_BASE_URL}${path}`;
  }
  return `${API_BASE_URL}/${path}`;
};

const getItemVariationAttributes = (item) => {
  if (!item) return {};

  const variationSources = [
    item.selectedVariation,
    item.selectedVariation?.variant,
    item.specifications,
    item.specifications?.selectedVariation,
  ].filter(Boolean);

  const keysToSkip = new Set([
    "_id",
    "id",
    "variantId",
    "variantKey",
    "price",
    "priceOverride",
    "price_override",
    "attributes",
    "selectedVariation",
    "selectedOptions",
    "quantity",
    "product",
    "productModel",
    "unitPrice",
    "totalPrice",
    "customizations",
    "specifications",
    "name",
    "thumbnail",
    "image",
    "variant",
  ]);

  const attributes = {};
  for (const source of variationSources) {
    if (source && typeof source === "object") {
      if (source.attributes && typeof source.attributes === "object") {
        Object.entries(source.attributes).forEach(([key, value]) => {
          if (!keysToSkip.has(key) && value !== undefined && value !== null && value !== "") {
            attributes[key] = value;
          }
        });
      }
      Object.entries(source).forEach(([key, value]) => {
        if (!keysToSkip.has(key) && value !== undefined && value !== null && value !== "") {
          attributes[key] = value;
        }
      });
    }
  }

  return attributes;
};

const getOrderItemImage = (item) => {
  if (!item) return null;
  const product = item.product || {};

  let resolvedImageUrl = null;
  let source = null;

  if (typeof product.thumbnail === "string" && product.thumbnail) {
    resolvedImageUrl = resolveImageUrl(product.thumbnail);
    source = "product.thumbnail";
  } else if (typeof product.image === "string" && product.image) {
    resolvedImageUrl = resolveImageUrl(product.image);
    source = "product.image";
  } else if (Array.isArray(product.images) && product.images.length > 0) {
    const first = product.images[0];
    if (typeof first === "string") {
      resolvedImageUrl = resolveImageUrl(first);
      source = "product.images[0]";
    } else if (first?.url) {
      resolvedImageUrl = resolveImageUrl(first.url);
      source = "product.images[0].url";
    }
  } else if (Array.isArray(product.additional_images) && product.additional_images.length > 0) {
    resolvedImageUrl = resolveImageUrl(product.additional_images[0]);
    source = "product.additional_images[0]";
  } else if (typeof item.thumbnail === "string" && item.thumbnail) {
    resolvedImageUrl = resolveImageUrl(item.thumbnail);
    source = "item.thumbnail";
  } else if (typeof item.image === "string" && item.image) {
    resolvedImageUrl = resolveImageUrl(item.image);
    source = "item.image";
  }

  console.log("OrderDetails image debug:", {
    itemId: item._id || item.id || null,
    productName: product?.name || item?.name || null,
    source,
    resolvedImageUrl,
  });

  return resolvedImageUrl;
};

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNotes, setStatusNotes] = useState("");

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    // Create AbortController for cleanup
    const controller = new AbortController();

    try {
      setLoading(true);
      console.log("🔍 Fetching order details for ID:", id);

      // Check if token exists
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("❌ No authentication token found");
        setError("Please login to access order details");
        return;
      }

      const response = await apiService.request(`/orders/${id}`, {
        signal: controller.signal,
      });
      console.log("📋 API Response:", response);
      console.log("📋 Response data:", response.data || response);

      if (response.data || response) {
        const orderData = response.data || response;
        setOrder(orderData);
        console.log("✅ Order set successfully");
        console.log("🔧 Order items count:", orderData.items?.length);
        console.log("🔧 Items with customizations:", orderData.items?.filter(item => item.customizations));
        console.log("🔧 Customizations data:", orderData.items?.map(item => ({
          product: item.product?.name || item.productModel || "Unknown",
          hasCustomizations: !!item.customizations,
          customizations: item.customizations
        })));
      } else {
        console.error("❌ No data in response");
        setError("Order data not found");
      }
    } catch (error) {
      // Don't set error if request was aborted
      if (error.name !== "AbortError") {
        console.error("❌ Error fetching order:", error);
        console.error("❌ Error details:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
        });

        if (error.response?.status === 404) {
          setError("Order not found");
        } else if (error.response?.status === 401) {
          setError("Authentication expired. Please login again.");
        } else {
          setError(
            error.response?.data?.message ||
              error.message ||
              "Failed to fetch order details",
          );
        }
      }
    } finally {
      setLoading(false);
    }

    // Cleanup function
    return () => {
      controller.abort();
    };
  };

  const handleStatusUpdate = async () => {
    // Validation: Check if status is selected
    if (!newStatus) {
      alert("Please select a status");
      return;
    }

    try {
      console.log("🔄 Updating order status to:", newStatus);
      console.log("🔄 Order ID:", id);
      
      await apiService.request(`/orders/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({
          status: newStatus,
          notes: statusNotes,
        }),
      });

      console.log("✅ Status update successful");
      await fetchOrderDetails(); // Refresh order details
      setStatusDialogOpen(false);
      setNewStatus("");
      setStatusNotes("");
    } catch (error) {
      console.error("❌ Status update failed:", error);
      console.error("❌ Error response:", error.response?.data);
      setError(error.response?.data?.message || "Failed to update status");
    }
  };

  const handleCancelOrder = async () => {
    if (
      !window.confirm(
        "Are you sure you want to cancel this order? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      console.log("🚫 Admin cancelling order:", id);

      const response = await apiService.request(`/orders/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          status: "cancelled",
          reason: "Cancelled by admin",
        }),
      });

      console.log("✅ Order cancelled successfully:", response);

      await fetchOrderDetails(); // Refresh order details
      alert("Order cancelled successfully");
    } catch (error) {
      console.error("❌ Error cancelling order:", error);
      setError(error.response?.data?.message || "Failed to cancel order");
    }
  };

  const handleArchiveOrder = async () => {
    if (
      !window.confirm(
        "Archive this order? The order will be hidden from normal admin and user lists but permanently preserved for records.",
      )
    ) {
      return;
    }

    try {
      console.log("📦 Admin archiving order:", id);

      const response = await apiService.request(`/orders/${id}/archive`, {
        method: "PUT",
      });

      console.log("✅ Order archived successfully:", response);

      await fetchOrderDetails(); // Refresh order details
      alert("Order archived successfully");
    } catch (error) {
      console.error("❌ Error archiving order:", error);
      setError(error.response?.data?.message || "Failed to archive order");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      confirmed: "info",
      processing: "secondary",
      shipped: "info",
      out_for_delivery: "warning",
      delivered: "success",
      returned: "error",
      cancelled: "error",
      failed: "error",
      canceled: "error",
      packaging: "secondary",
    };
    return colors[status] || "default";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        p={3}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Loading order details...
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Order ID: {id}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/dashboard/orders")}
            >
              Back to Orders
            </Button>
          }
        >
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Order ID: {id}
        </Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box p={3}>
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/dashboard/orders")}
            >
              Back to Orders
            </Button>
          }
        >
          Order not found
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Order ID: {id}
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={1}
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: { xs: "space-between", sm: "flex-start" },
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/dashboard/orders")}
            sx={{
              alignSelf: { xs: "flex-start", sm: "auto" },
              width: { xs: "auto", sm: "auto" },
              minWidth: { xs: 120, sm: "auto" },
            }}
          >
            Back to Orders
          </Button>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Chip
              label={order.status?.replace("-", " ").toUpperCase() || "PENDING"}
              color={getStatusColor(order.status)}
              sx={{ alignSelf: { xs: "flex-end", sm: "auto" } }}
            />
            {order.archived && (
              <Chip
                label="ARCHIVED"
                color="error"
                size="small"
                sx={{ alignSelf: { xs: "flex-end", sm: "auto" } }}
              />
            )}
          </Box>
        </Box>
        <Typography
          variant={{ xs: "h6", sm: "h4" }}
          component="h1"
          sx={{
            fontSize: { xs: "1.1rem", sm: "1.5rem", md: "2rem" },
            textAlign: { xs: "center", sm: "left" },
            width: "100%",
          }}
        >
          Order #{order.orderNumber || order._id}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Order Information */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              border: "1px solid #e0e0e0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Order Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Order Number
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                  {order.orderNumber || order._id}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Order Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(order.createdAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography variant="body1">
                  ₹{order.totalAmount?.toFixed(2) || "0.00"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Payment Status
                </Typography>
                <Typography variant="body1">
                  {order.paymentStatus || "Pending"}
                </Typography>
              </Grid>
            </Grid>

            <Box mt={2}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1, sm: 2 },
                  alignItems: { xs: "stretch", sm: "center" },
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => setStatusDialogOpen(true)}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Update Status
                </Button>
                {order.status === "pending" && !order.archived && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => handleCancelOrder()}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Cancel Order
                  </Button>
                )}
                {!order.archived && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<CancelIcon />}
                    onClick={() => handleArchiveOrder()}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Archive Order
                  </Button>
                )}
              </Box>
              {order.archived && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, fontStyle: "italic" }}
                >
                  This order has been archived for record-keeping and is hidden
                  from the standard admin and user order lists.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Customer Information */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              border: "1px solid #e0e0e0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box display="flex" alignItems="center" mb={2}>
              <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
              <Typography variant="body1">
                {order.customer
                  ? order.customer.fullName || order.customer.name || `${order.customer.firstName || ""} ${order.customer.lastName || ""}`
                  : "Guest User"}
              </Typography>
            </Box>

            {order.customer?.email && (
              <Box display="flex" alignItems="center" mb={2}>
                <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Typography variant="body2">{order.customer.email}</Typography>
              </Box>
            )}

            <Box display="flex" alignItems="center" mb={2}>
              <PhoneIcon sx={{ mr: 1, color: "text.secondary" }} />
              <Typography 
                variant="body2"
                sx={{ 
                  color: (order.customer?.phone || order.shippingAddress?.phone) ? "text.primary" : "error.main",
                  fontWeight: (order.customer?.phone || order.shippingAddress?.phone) ? 400 : 500
                }}
              >
                {order.customer?.phone || order.shippingAddress?.phone
                  ? (order.customer?.phone || order.shippingAddress?.phone)
                  : "📱 Not Available"}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Shipping Address */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              border: "1px solid #e0e0e0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Shipping Address
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Contact Person */}
            {order.shippingAddress?.firstName && (
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Typography variant="body1">
                  {order.shippingAddress.firstName} {order.shippingAddress.lastName}
                </Typography>
              </Box>
            )}

            {/* Email */}
            {order.shippingAddress?.email && (
              <Box display="flex" alignItems="center" mb={2}>
                <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Typography variant="body2">{order.shippingAddress.email}</Typography>
              </Box>
            )}

            {/* Phone */}
            {order.shippingAddress?.phone && (
              <Box display="flex" alignItems="center" mb={2}>
                <PhoneIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Typography variant="body2">{order.shippingAddress.phone}</Typography>
              </Box>
            )}

            {/* Address */}
            <Box display="flex" alignItems="flex-start" gap={1}>
              <LocationIcon sx={{ mt: 0.5, color: "text.secondary" }} />
              <Box>
                {order.shippingAddress?.street && (
                  <Typography variant="body1">
                    {order.shippingAddress.street}
                  </Typography>
                )}
                <Typography variant="body2">
                  {order.shippingAddress?.city}, {order.shippingAddress?.state}
                </Typography>
                <Typography variant="body2">
                  {order.shippingAddress?.zipCode}
                </Typography>
                <Typography variant="body2">
                  {order.shippingAddress?.country}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Event Information */}
        {order.eventInfo && (
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <Typography variant="h6" gutterBottom>
                Event Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box display="flex" alignItems="center" mb={2}>
                <EventIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Typography variant="body1">
                  {order.eventInfo.eventType?.charAt(0).toUpperCase() +
                    order.eventInfo.eventType?.slice(1)}{" "}
                  Event
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Event Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(order.eventInfo.eventDate).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Event Time
                  </Typography>
                  <Typography variant="body1">
                    {order.eventInfo.eventTime}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Venue Type
                  </Typography>
                  <Typography variant="body1">
                    {order.eventInfo.venueType?.charAt(0).toUpperCase() +
                      order.eventInfo.venueType?.slice(1)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Guest Count
                  </Typography>
                  <Typography variant="body1">
                    {order.eventInfo.guestCount} guests
                  </Typography>
                </Grid>
              </Grid>

              {order.eventInfo.venueAddress && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Venue Address
                  </Typography>
                  <Typography variant="body2">
                    {order.eventInfo.venueAddress}
                  </Typography>
                </Box>
              )}

              {order.eventInfo.specialInstructions && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Special Instructions
                  </Typography>
                  <Typography variant="body2">
                    {order.eventInfo.specialInstructions}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Order Items */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 3,
              border: "1px solid #e0e0e0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Order Items
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <List>
              {order.items?.map((item, index) => {
                const itemImageUrl = getOrderItemImage(item);
                const variationAttributes = getItemVariationAttributes(item);
                console.log("OrderDetails item render:", {
                  index,
                  itemId: item._id || item.id || null,
                  productName: item.product?.name || null,
                  itemImageUrl,
                  variationAttributes,
                  willShowImage: Boolean(itemImageUrl),
                });
                return (
                  <ListItem
                    key={index}
                    divider
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      py: 2,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={2}>
                          {itemImageUrl && (
                            <Box
                              component="img"
                              src={itemImageUrl}
                              alt={item.product?.name || `Product ${index + 1}`}
                              crossOrigin="anonymous"
                              referrerPolicy="no-referrer"
                              sx={{
                                width: 88,
                                height: 88,
                                objectFit: "cover",
                                borderRadius: 2,
                                border: "1px solid #e5e7eb",
                                backgroundColor: "#f8fafc",
                              }}
                              onError={(e) => {
                                console.warn("OrderDetails image load failed:", itemImageUrl, e);
                                e.target.onerror = null;
                                e.target.src = "/placeholder.png";
                              }}
                            />
                          )}
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {item.product?.name || `Product ${index + 1}`}
                            </Typography>
                            {item.customizations && (
                              <Chip
                                label="Has Customizations"
                                size="small"
                                color="primary"
                                sx={{ mt: 1 }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box mt={itemImageUrl ? 1 : 0}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            Quantity: {item.quantity} ×{" "}
                            {formatCurrency(item.unitPrice || item.price || 0)}
                          </Typography>
                          <Typography
                            variant="body1"
                            color="primary"
                            sx={{ mb: 1 }}
                          >
                            Total:{" "}
                            {formatCurrency(
                              item.totalPrice ||
                                item.quantity *
                                  (item.unitPrice || item.price || 0),
                            )}
                          </Typography>
                          {item.productModel && (
                            <Chip
                              label={item.productModel}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {Object.keys(variationAttributes).length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {Object.entries(variationAttributes).map(([key, value]) => (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                  key={key}
                                >
                                  {`${key
                                    .replace(/([A-Z])/g, " $1")
                                    .replace(/^./, (char) => char.toUpperCase())}: ${value}`}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                </ListItem>
              )})}
            </List>
          </Paper>
        </Grid>

        {/* Product Customizations */}
        {order.items?.some(item => item.customizations) && (
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                border: "2px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                backgroundColor: "#fafafa",
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#1976d2" }}>
                ✨ Product Customizations
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {order.items?.map((item, index) => 
                item.customizations ? (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                      Customization for: {item.product?.name || `Product ${index + 1}`}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                      {item.customizations?.enabled !== undefined && (
                        <Chip
                          size="small"
                          color={item.customizations.enabled ? "success" : "default"}
                          label={item.customizations.enabled ? "Customized" : "Customization disabled"}
                        />
                      )}
                      {item.customizations?.type && (
                        <Chip size="small" variant="outlined" label={`Type: ${item.customizations.type}`} />
                      )}
                      {item.customizations?.status && (
                        <Chip size="small" variant="outlined" label={`Status: ${item.customizations.status}`} />
                      )}
                    </Box>
 
                    <Grid container spacing={2}>
                      {/* Customer Preview Image */}
                      {item.customizations?.previewImage && (
                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              border: "2px solid #1976d2",
                              borderRadius: 1,
                              p: 2,
                              backgroundColor: "#e3f2fd",
                            }}
                          >
                            <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                              👁️ Customer Final Preview
                            </Typography>
                            <Box sx={{ textAlign: "center" }}>
                              <img
                                src={item.customizations.previewImage}
                                alt="Customer customization preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "300px",
                                  borderRadius: 4,
                                  display: "block",
                                  margin: "0 auto",
                                  border: "1px solid #90caf9",
                                }}
                                onError={(e) => {
                                  console.error("Preview image failed to load:", item.customizations.previewImage);
                                  e.target.alt = "Preview failed to load";
                                  e.target.style.display = "block";
                                  e.target.style.color = "red";
                                  e.target.style.padding = "20px";
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                Exact preview customer saw when saving
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Customization Images */}
                      {(item.customizations.image || (Array.isArray(item.customizations.positions) && item.customizations.positions.length > 0)) && (
                        <Grid item xs={12} sm={item.customizations?.previewImage ? 6 : 6}>
                          <Box
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              p: 2,
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              📷 Customized Images
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                              {item.customizations.image && (
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography variant="caption" color="text.secondary">Primary</Typography>
                                  <img
                                    src={item.customizations.image}
                                    alt="Customization"
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "180px",
                                      borderRadius: 4,
                                      display: "block",
                                      marginTop: 4,
                                    }}
                                    onError={(e) => {
                                      console.error("Image failed to load:", item.customizations.image);
                                      e.target.alt = "Image failed to load";
                                      e.target.style.display = "block";
                                      e.target.style.color = "red";
                                      e.target.style.padding = "20px";
                                    }}
                                  />
                                </Box>
                              )}
                              {Array.isArray(item.customizations.positions) && item.customizations.positions.map((positionEntry, positionIndex) => (
                                positionEntry.image ? (
                                  <Box key={`${positionEntry.position || positionIndex}`} sx={{ textAlign: "center" }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {positionEntry.position || `Position ${positionIndex + 1}`}
                                    </Typography>
                                    <img
                                      src={positionEntry.image}
                                      alt={positionEntry.position || `Position ${positionIndex + 1}`}
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "180px",
                                        borderRadius: 4,
                                        display: "block",
                                        marginTop: 4,
                                      }}
                                    />
                                  </Box>
                                ) : null
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Customization Details */}
                      <Grid item xs={12} sm={item.customizations.image || (Array.isArray(item.customizations.positions) && item.customizations.positions.length > 0) ? 6 : 12}>
                        <Box
                          sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 2,
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          {item.customizations.text && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                ✏️ Custom Text
                              </Typography>
                              <Typography variant="body1" sx={{ wordBreak: "break-word", fontWeight: 500 }}>
                                {item.customizations.text}
                              </Typography>
                            </Box>
                          )}

                          {item.customizations.printArea && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                📍 Print Area
                              </Typography>
                              <Chip
                                label={item.customizations.printArea}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          )}

                          {(item.customizations.color || item.color) && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                🎨 Color
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {item.customizations.color || item.color}
                              </Typography>
                            </Box>
                          )}

                          {(item.customizations.size || item.size) && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                📏 Size
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {item.customizations.size || item.size}
                              </Typography>
                            </Box>
                          )}

                          {Array.isArray(item.customizations.positions) && item.customizations.positions.length > 0 && (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                🖼️ Saved Positions
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {item.customizations.positions.map((positionEntry, positionIndex) => (
                                  <Chip
                                    key={`${positionEntry.position || positionIndex}`}
                                    label={`${positionEntry.position || `Position ${positionIndex + 1}`}: ${positionEntry.text || "image"}`}
                                    variant="outlined"
                                    size="small"
                                    sx={{ mb: 1 }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Text Details */}
                          {Array.isArray(item.customizations.positions) &&
                            item.customizations.positions.some((p) => p?.text) && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                  ✍️ Custom Text Details
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                  {item.customizations.positions
                                    .filter((positionEntry) => positionEntry?.text)
                                    .map((positionEntry, index) => (
                                      <Box
                                        key={index}
                                        sx={{
                                          border: "1px solid #e0e0e0",
                                          borderRadius: 1,
                                          p: 2,
                                          backgroundColor: "#fafafa",
                                        }}
                                      >
                                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                          {positionEntry.position || "Position"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                          Text: {positionEntry.text}
                                        </Typography>
                                        {positionEntry.font && (
                                          <Typography variant="caption" color="text.secondary">
                                            Font: {positionEntry.font}
                                          </Typography>
                                        )}
                                        {positionEntry.color && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                            Color: {positionEntry.color}
                                          </Typography>
                                        )}
                                        {positionEntry.layout && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                            Layout: {positionEntry.layout}
                                          </Typography>
                                        )}
                                        {Array.isArray(positionEntry.layers) && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                            Layers: {positionEntry.layers.length}
                                          </Typography>
                                        )}
                                      </Box>
                                    ))}
                                </Box>
                              </Box>
                            )}

                          {/* Additional Instructions */}
                          {(item.customizations?.customNotes || item.specifications?.customNotes) && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                📝 Additional Instructions
                              </Typography>
                              <Box
                                sx={{
                                  border: "1px solid #e0e0e0",
                                  borderRadius: 1,
                                  p: 2,
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                  {item.customizations?.customNotes || item.specifications?.customNotes}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                    {index < order.items.length - 1 && <Divider sx={{ mt: 3 }} />}
                  </Box>
                ) : null
              )}
            </Paper>
          </Grid>
        )}

        {/* Fallback: If customizations exist but weren't rendered above */}
        {!order.items?.some(item => item.customizations) && order.items?.some(item => item.customization) && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Found customization data with alternate structure. Customizations might be stored differently than expected.
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Pricing Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              border: "1px solid #e0e0e0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Pricing Breakdown
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">
                  {formatCurrency(order.totalAmount || 0)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Tax:</Typography>
                <Typography variant="body2">{formatCurrency(0)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Shipping:</Typography>
                <Typography variant="body2">{formatCurrency(0)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(order.totalAmount || 0)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Status Timeline */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              border: "1px solid #e0e0e0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Status Timeline
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {order.timeline && order.timeline.length > 0 ? (
              <List>
                {order.timeline.map((timelineItem, index) => (
                  <ListItem key={index} sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={timelineItem.status
                              ?.replace("-", " ")
                              .toUpperCase()}
                            color={getStatusColor(timelineItem.status)}
                            size="small"
                          />
                          <Typography variant="body2">
                            {formatDate(timelineItem.timestamp)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        timelineItem.notes && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            {timelineItem.notes}
                          </Typography>
                        )
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No status updates yet
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                label="New Status"
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="shipped">Shipped</MenuItem>
                <MenuItem value="out_for_delivery">Out for Delivery</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderDetails;
